import { useQuery } from '@tanstack/react-query';
import api from './authService';

// Get vendor consultation settings (public - for client)
export const getVendorConsultationSettings = async (vendorId) => {
  const response = await api.get(`/vendors/${vendorId}/consultation-settings`);
  return response.data;
};

// Client: Book a consultation
export const bookConsultation = async (data) => {
  const response = await api.post('/consultations', data);
  return response.data;
};

// Client: Active consultation with a vendor (pending or accepted)
export const getClientConsultationWithVendor = async (vendorId) => {
  const response = await api.get(`/consultations/with-vendor/${vendorId}`);
  return response.data;
};

// Vendor: Get consultation settings
export const getConsultationSettings = async () => {
  const response = await api.get('/vendor/consultations/settings');
  return response.data;
};

// Vendor: Check if Google Meet auto-generation is configured
export const getGoogleMeetConfigured = async () => {
  const response = await api.get('/vendor/consultations/google-meet-configured');
  return response.data;
};

// Vendor: Update consultation settings
export const updateConsultationSettings = async (data) => {
  const response = await api.put('/vendor/consultations/settings', data);
  return response.data;
};

// Vendor: Lead status catalog + call outcomes
export const getLeadCatalog = async () => {
  const response = await api.get('/vendor/consultations/catalog');
  return response.data;
};

// Vendor: List consultations / leads
export const getConsultations = async (params = {}) => {
  const response = await api.get('/vendor/consultations', { params });
  return response.data;
};

// Vendor: Consultation leads on calendar (not bookings)
export const getConsultationCalendarLeads = async (params = {}) => {
  const response = await api.get('/vendor/consultations/calendar', { params });
  return response.data;
};

/** Pending internal lead total for sidebar badges */
export const getPendingConsultationLeadCount = async () => {
  const response = await getConsultations({ status: 'pending', per_page: 1 });
  return Number(response?.data?.total ?? 0) || 0;
};

export const usePendingConsultationLeadCount = (options = {}) =>
  useQuery({
    queryKey: ['pendingConsultationLeadCount'],
    queryFn: getPendingConsultationLeadCount,
    staleTime: 30_000,
    ...options,
  });

// Vendor: Single lead detail
export const getConsultation = async (id) => {
  const response = await api.get(`/vendor/consultations/${id}`);
  return response.data;
};

// Vendor: Today's / filtered reminders
export const getConsultationReminders = async (params = {}) => {
  const response = await api.get('/vendor/consultations/reminders', { params });
  return response.data;
};

// Vendor: Loss insights
export const getLeadLossInsights = async () => {
  const response = await api.get('/vendor/consultations/insights/loss');
  return response.data;
};

// Vendor: Change lead status
export const updateLeadStatus = async (id, data) => {
  const response = await api.patch(`/vendor/consultations/${id}/status`, data);
  return response.data;
};

// Vendor: Change consultation date/time
export const updateLeadSchedule = async (id, data) => {
  const response = await api.patch(`/vendor/consultations/${id}/schedule`, data);
  return response.data;
};

// Vendor: Update stage checklist
export const updateLeadChecklist = async (id, checklist, extras = {}) => {
  const response = await api.patch(`/vendor/consultations/${id}/checklist`, {
    checklist,
    ...extras,
  });
  return response.data;
};

// Vendor: Add note
export const addLeadNote = async (id, note) => {
  const response = await api.post(`/vendor/consultations/${id}/notes`, { note });
  return response.data;
};

// Vendor: Log follow-up email sent
export const logLeadEmail = async (id, subject = null) => {
  const response = await api.post(`/vendor/consultations/${id}/log-email`, { subject });
  return response.data;
};

// Vendor: Log call outcome
export const logLeadCall = async (id, data) => {
  const response = await api.post(`/vendor/consultations/${id}/log-call`, data);
  return response.data;
};

// Vendor: Save agreed deal summary
export const saveLeadDeal = async (id, deal_summary) => {
  const response = await api.post(`/vendor/consultations/${id}/deal`, { deal_summary });
  return response.data;
};

// Vendor: Complete / dismiss a reminder
export const completeConsultationReminder = async (reminderId, data) => {
  const response = await api.post(`/vendor/consultations/reminders/${reminderId}/complete`, data);
  return response.data;
};

// Vendor: Accept consultation (legacy → Meeting Scheduled + meet link)
export const acceptConsultation = async (id, data) => {
  const response = await api.post(`/vendor/consultations/${id}/accept`, data);
  return response.data;
};

// Vendor: Reject consultation
export const rejectConsultation = async (id, data = {}) => {
  const response = await api.post(`/vendor/consultations/${id}/reject`, data);
  return response.data;
};

// Vendor: Mark meeting done (legacy complete)
export const completeConsultation = async (id) => {
  const response = await api.post(`/vendor/consultations/${id}/complete`);
  return response.data;
};

// Vendor: Delete a lead / consultation
export const deleteConsultation = async (id) => {
  const response = await api.delete(`/vendor/consultations/${id}`);
  return response.data;
};
